'use client'
import { useState, useRef, useEffect } from 'react'
import Icon from './Icon'

interface DropdownProps {
  value: string
  onChange: (value: string) => void
  options: string[]
  placeholder: string
  error?: string
}

export default function Dropdown({ value, onChange, options, placeholder, error }: DropdownProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between bg-white/[0.04] border ${error ? 'border-red-500/50' : 'border-white/[0.08]'} rounded-lg px-4 py-2.5 text-sm ${value ? 'text-white' : 'text-[#475569]'} focus:outline-none focus:border-[#4f46e5]/50 transition-colors cursor-pointer`}
      >
        <span>{value || placeholder}</span>
        <Icon name="chevron-down" className={`w-4 h-4 text-[#64748b] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1.5 bg-[#0c0c1a]/95 backdrop-blur-xl border border-white/[0.08] rounded-lg shadow-2xl max-h-60 overflow-y-auto custom-scrollbar">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false) }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                value === opt
                  ? 'text-white bg-white/[0.06]'
                  : 'text-[#94a3b8] hover:text-white hover:bg-white/[0.04]'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  )
}
